import {
  CoinType,
  IBalanceUpdaterRegistrator,
  INumberFormatter,
  ISimpleUserInfoHolder,
  NUMBER_FORMATTER_TOKEN,
  T_IBalanceUpdaterRegistrator,
  T_ISimpleUserInfoHolder,
} from '@cgs/common';
import { Decimal } from '@cgs/shared';
import {
  ActionActivator,
  ActionFactory,
  Container,
  EmptyAction,
  SceneObject,
  TextSceneObject,
  Vector2,
} from '@cgs/wge';
import { RESOURCES_COMPONENT_TOKEN } from '../../type_definitions';
import { GameComponentProvider } from './game_component_provider';
import { ResourcesComponent } from './resources_component';

export interface IBalanceUpdater {
  decreaseBalanceAndStopUpdate(decreaseAmount: number): void;
  updateBalance(): void;
  unlockBalance(): void;
  lockBalance(lockAmount: number): void;
  resumeUpdateWithDisplayed(animate?: boolean): void;
  isBalanceEnough(sum: number): boolean;
  addBalanceToShown(sumToAdd: number): void;
  resumeUpdateWithCoin(startPosition: Vector2, coinType?: CoinType): void;
  resumeUpdate(animate?: boolean): void;
}

export class BalanceListenerProvider extends GameComponentProvider {
  protected readonly balanceUpdaterRegistrator: IBalanceUpdaterRegistrator;
  protected slotBalanceListener: SlotBalanceListener | null;

  constructor(container: Container) {
    super();
    this.balanceUpdaterRegistrator = container.resolve(T_IBalanceUpdaterRegistrator);
    this.slotBalanceListener = new SlotBalanceListener(container);
    this.balanceUpdaterRegistrator.register(this.slotBalanceListener);
  }

  public deinitialize(): void {
    this.unregisterSlotBalanceListener();
  }

  protected unregisterSlotBalanceListener(): void {
    if (this.slotBalanceListener) {
      this.balanceUpdaterRegistrator.unregister(this.slotBalanceListener);
      this.slotBalanceListener = null;
    }
  }
}

export class SlotBalanceListener implements IBalanceUpdater {
  protected _lastShownBalance: number = 0;
  protected readonly _coins: TextSceneObject[];
  protected readonly _numberFormatter: INumberFormatter;

  private _userInfoHolder: ISimpleUserInfoHolder;
  private _lockedBalance: number = 0;
  private _root: SceneObject | null;
  private _activator: ActionActivator;
  private _targetBalance: number = 0;

  public get currentBalance(): number {
    return this._userInfoHolder.user!.balance - this._lockedBalance;
  }

  constructor(container: Container) {
    this._root = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).footer;
    this._userInfoHolder = container.resolve<ISimpleUserInfoHolder>(T_ISimpleUserInfoHolder);
    this._numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);
    this._coins = this._root?.findAllById<TextSceneObject>('text_format_Coins') || [];
    if (!this._coins.length) {
      this._coins = this._root?.findAllById<TextSceneObject>('Coins') || [];
    }
    this._activator = ActionActivator.withAction(
      this._root || new SceneObject(),
      new EmptyAction()
    );
    this.resumeUpdate(false);
  }

  public getShownBalance(): number {
    return this._targetBalance;
  }

  public alreadyUpdatingBalanceToTarget(balance: number): boolean {
    return this._activator.started && Math.abs(this._targetBalance - balance) < 0.1;
  }

  public displayBalance(balance: number): void {
    this._lastShownBalance = balance;
    for (const coin of this._coins) {
      coin.text = this._numberFormatter.formatMoney(balance);
    }
  }

  public setBalance(balance: number, animate: boolean): void {
    if (this.alreadyUpdatingBalanceToTarget(balance)) {
      return;
    }
    this._targetBalance = balance;

    if (animate) {
      this._activator.stop();

      const action = ActionFactory.CreateInterpolateDouble()
        .withValues(this._lastShownBalance, balance)
        .withDuration(0.5);
      action.valueChange.listen((value: number) => {
        this.displayBalance(value);
      });

      action.done.listen(() => console.log('Balance update finished: ' + balance.toString()));

      this._activator = ActionActivator.withAction(this._root || new SceneObject(), action);

      this._activator.start();
    } else {
      this._activator.stop();
      this.displayBalance(balance);
    }
  }

  public decreaseBalanceAndStopUpdate(decreaseAmount: number): void {
    const visibleBalance = this.getShownBalance();
    if (Math.abs(visibleBalance - this.currentBalance) >= 1) {
      this.setBalance(this.currentBalance, false);
    }

    const targetBalance = new Decimal(this.currentBalance).sub(decreaseAmount).toNumber();
    if (targetBalance < 0) {
      throw new Error(
        `Can't afford the bet of ${decreaseAmount} with the balance ${this.currentBalance}`
      );
    }

    this.setBalance(targetBalance, false);
  }

  public updateBalance(): void {
    if (this.getShownBalance() !== this.currentBalance) {
      this.setBalance(this._userInfoHolder.user!.balance, false);
    }
  }

  public unlockBalance(): void {
    this._lockedBalance = 0.0;
  }

  public lockBalance(lockAmount: number): void {
    this._lockedBalance = lockAmount;
  }

  public resumeUpdateWithDisplayed(animate: boolean = true): void {
    this._userInfoHolder.user!.balance = this._targetBalance;
    this.resumeUpdate(animate);
  }

  public isBalanceEnough(sum: number): boolean {
    return !!this._userInfoHolder.user && this._userInfoHolder.user.balance >= sum;
  }

  public addBalanceToShown(sumToAdd: number): void {
    this.setBalance(this._targetBalance + sumToAdd, true);
  }

  public resumeUpdateWithCoin(startPosition: Vector2, _coinType: CoinType = CoinType.Spiral): void {
    this.setBalance(this.currentBalance, true);
  }

  public resumeUpdate(animate: boolean = true): void {
    this.setBalance(this.currentBalance, animate);
  }
}
